/**
 * Hook that orchestrates the subscription payment flow:
 *
 * Step 1: POST InitiateSubscriptionPayment → get Moyasar config
 * Step 2: Render Moyasar form (handled by MoyasarPaymentForm component)
 * Step 3: on_completed fires → POST OnPaymentInitiated (link payment & save token)
 * Step 4: Moyasar SDK automatically redirects user to callback_url for 3DS
 * Step 5: Webhook finalizes status (backend only)
 * Step 6: PaymentCallbackView polls GET GetTransactionStatus
 */

import { useCallback, useState } from 'react';
import { useInitiateSubscriptionPayment, useOnPaymentInitiated } from '../services';
import { IInitiatePaymentResponse, IMoyasarPayment } from '../types';

interface UseSubscriptionPaymentOptions {
  planId: number;
}

interface UseSubscriptionPaymentResult {
  paymentConfig: IInitiatePaymentResponse | null;
  isInitiating: boolean;
  isProcessing: boolean;
  initError: Error | null;
  initiatePayment: () => void;
  handlePaymentCompleted: (payment: IMoyasarPayment) => Promise<void>;
}

export const useSubscriptionPayment = ({
  planId,
}: UseSubscriptionPaymentOptions): UseSubscriptionPaymentResult => {
  const [paymentConfig, setPaymentConfig] = useState<IInitiatePaymentResponse | null>(null);

  const {
    mutate: initiate,
    isPending: isInitiating,
    error: initError,
  } = useInitiateSubscriptionPayment();

  const {
    mutateAsync: onPaymentInitiated,
    isPending: isProcessing,
  } = useOnPaymentInitiated();

  // Step 1: Initiate payment — call backend to create transaction
  const initiatePayment = useCallback(() => {
    initiate(
      { planId },
      {
        onSuccess: (data) => {
          setPaymentConfig(data);
        },
      }
    );
  }, [planId, initiate]);

  // Step 3: Called by on_completed — link provider payment & save card token.
  // After this returns, Moyasar SDK automatically redirects to callback_url for 3DS (Step 4).
  // We do NOT navigate manually — the SDK handles the redirect.
  const handlePaymentCompleted = useCallback(
    async (payment: IMoyasarPayment) => {
      if (!paymentConfig) return;

      const payload = {
        transactionId: paymentConfig.transactionGuid,
        providerPaymentId: payment.id,
        providerPaymentStatus: payment.status,
        source: payment.source,
      };

      try {
        await onPaymentInitiated(payload);
      } catch (err) {
        // Even if OnPaymentInitiated fails, the webhook will handle it.
        // Moyasar SDK will still redirect to callback_url for 3DS.
        console.error('OnPaymentInitiated failed:', err);
      }
      // Moyasar SDK redirects to callback_url after this function returns.
    },
    [paymentConfig, onPaymentInitiated]
  );

  return {
    paymentConfig,
    isInitiating,
    isProcessing,
    initError: initError as Error | null,
    initiatePayment,
    handlePaymentCompleted,
  };
};
